import { Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PetrocontrolLogo from "@/components/PetrocontrolLogo";

export default function Footer({ onBookingOpen }) {
  const { t } = useLanguage();
  const settings = useSiteSettings();
  const ft = t("footer");
  const items = t("practiceAreas.items") || [];
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const NAV_LINKS = [
    { label: t("nav.services"), href: "#practice-areas" },
    { label: t("nav.about"), href: "#about" },
    { label: t("nav.team"), href: "#attorneys" },
    { label: t("nav.results"), href: "#results" },
  ];

  const scrollTo = (href) => {
    if (!isHome) {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer data-testid="footer" role="contentinfo" className="bg-[#152238] text-white pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-16 pb-10 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2.5 mb-6 md:mb-0">
            <PetrocontrolLogo className="h-8" variant="light" />
          </Link>
          <Button
            data-testid="footer-cta"
            onClick={onBookingOpen}
            variant="outline"
            className="border-white/20 text-white hover:bg-white hover:text-[#152238] rounded-full px-8 py-5 text-sm font-medium transition-all duration-300"
          >
            {ft.cta}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="md:col-span-1">
            <p className="text-white/50 text-sm leading-relaxed">{settings.address_line1}<br />{settings.address_line2}</p>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-wider text-white/40 mb-5 font-semibold">{ft.contactTitle}</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-white/40" /><a href={`tel:${settings.phone.replace(/[^+\d]/g, "")}`} data-testid="footer-phone" className="text-white/60 hover:text-white text-sm transition-colors">{settings.phone}</a></li>
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-white/40" /><a href={`mailto:${settings.email}`} data-testid="footer-email" className="text-white/60 hover:text-white text-sm transition-colors">{settings.email}</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-wider text-white/40 mb-5 font-semibold">{ft.menuTitle}</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((link) => (
                <li key={link.label}><button onClick={() => scrollTo(link.href)} className="text-white/60 hover:text-white text-sm transition-colors">{link.label}</button></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-wider text-white/40 mb-5 font-semibold">{ft.practiceAreasTitle}</h4>
            <ul className="space-y-3">
              {items.slice(0, 4).map((area) => (
                <li key={area.id}>
                  <Link to={`/services/${area.id}`} className="text-white/60 hover:text-white text-sm transition-colors">{area.title}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/30 text-xs text-center md:text-left">
              {ft.copyright.replace("{year}", new Date().getFullYear())}
            </p>
            <p data-testid="footer-disclaimer" className="text-white/20 text-xs text-center md:text-right max-w-lg">{ft.disclaimer}</p>
          </div>
          <div className="mt-6 text-center">
            <Link 
              to="/admin" 
              data-testid="footer-admin-link"
              className="text-white/30 hover:text-white/60 text-xs transition-colors"
            >
              Solución Petrocontrol
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
